import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import { trackEvent } from '../../../client/analytics';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

const EVENT_IDS = {
  CLICK_ADD_FEE: '6717',
  CLICK_EDIT_FEE: '6718',
  CLICK_SAVE_FEE: '6719',
  CLICK_CANCEL_FEE_EDIT: '6720',
  CLICK_DELETE_FEE: '6721',
  CONFIRM_DELETE_FEE: '6722',
  CLICK_ADD_DISCLAIMER: '6723',
  CLICK_EDIT_DISCLAIMER: '6725',
  CLICK_SAVE_DISCLAIMER: '6726',
  CLICK_VIEW_FEE_DETAILS: '8232',
} as const;

const TRIGGER_SOURCES = {
  BUTTON_TO_EDIT_FEE: 'button_to_edit_fee',
  VIEW_DETAILS_BUTTON_TO_EDIT_FEE: 'view_details_button_to_edit_fee',
  BUTTON_TO_VIEW_FEE_DETAILS: 'button_to_view_fee_details',
  LIST_ITEM_VIEW_FEE_DETAILS: 'list_item_view_fee_details',
} as const;

// https://dataportal.zillowgroup.net/event-registry/6717
const ClickAddFee = ({
  currentProperty,
  user,
  triggerObject = 'zram_costs_and_fees',
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerObject?: string;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.CLICK_ADD_FEE,
      event_type_version_id: '2',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'zram_posting_path',
      trigger_source_nm: 'button_to_add_fee',
      trigger_object_nm: triggerObject,
    },
    semantic: {
      semantic_event_nm: 'add_fee',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6718
const ClickEditFee = ({
  currentProperty,
  user,
  triggerSource = TRIGGER_SOURCES.BUTTON_TO_EDIT_FEE,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerSource?: typeof TRIGGER_SOURCES.BUTTON_TO_EDIT_FEE | typeof TRIGGER_SOURCES.VIEW_DETAILS_BUTTON_TO_EDIT_FEE;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_EDIT_FEE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: triggerSource,
    },
    semantic: {
      semantic_event_nm: 'edit_fee',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6719
const ClickSaveFee = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_SAVE_FEE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'button_to_save_fee',
    },
    semantic: {
      semantic_event_nm: 'save_fee',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6720
const ClickCancelFeeEdit = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_CANCEL_FEE_EDIT,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'cancel_fee_edit',
    },
    semantic: {
      semantic_event_nm: 'cancel_fee_edit',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6721
const ClickDeleteFee = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_DELETE_FEE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'button_to_delete_fee',
    },
    semantic: {
      semantic_event_nm: 'delete_fee',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6722
const ConfirmDeleteFee = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CONFIRM_DELETE_FEE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'button_to_confirm_fee_deletion',
    },
    semantic: {
      semantic_event_nm: 'confirm_delete_fee',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6723
const ClickAddDisclaimer = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_ADD_DISCLAIMER,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'button_to_add_disclaimer',
    },
    semantic: {
      semantic_event_nm: 'add_disclaimer',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6725
const ClickEditDisclaimer = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_EDIT_DISCLAIMER,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'button_to_edit_disclaimer',
    },
    semantic: {
      semantic_event_nm: 'edit_disclaimer',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6726
const ClickSaveDisclaimer = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_SAVE_DISCLAIMER,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: 'button_to_save_disclaimer',
    },
    semantic: {
      semantic_event_nm: 'save_disclaimer',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8232
const ClickViewFeeDetails = ({
  currentProperty,
  user,
  triggerSource = TRIGGER_SOURCES.LIST_ITEM_VIEW_FEE_DETAILS,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerSource?: typeof TRIGGER_SOURCES.BUTTON_TO_VIEW_FEE_DETAILS | typeof TRIGGER_SOURCES.LIST_ITEM_VIEW_FEE_DETAILS;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.CLICK_VIEW_FEE_DETAILS,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zram_posting_path',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'zram_costs_and_fees',
      trigger_source_nm: triggerSource,
    },
    semantic: {
      semantic_event_nm: 'view_fee_details',
      topic_tag_txt: ['costs_and_fees'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

const CostsAndFeesMFCSEvents = {
  ClickAddFee,
  ClickEditFee,
  ClickSaveFee,
  ClickCancelFeeEdit,
  ClickDeleteFee,
  ConfirmDeleteFee,
  ClickAddDisclaimer,
  ClickEditDisclaimer,
  ClickSaveDisclaimer,
  ClickViewFeeDetails,
  TRIGGER_SOURCES,
};

// Clickstream visualizer data-eventid attributes
ClickAddFee.data_event_id = EVENT_IDS.CLICK_ADD_FEE;
ClickEditFee.data_event_id = EVENT_IDS.CLICK_EDIT_FEE;
ClickSaveFee.data_event_id = EVENT_IDS.CLICK_SAVE_FEE;
ClickCancelFeeEdit.data_event_id = EVENT_IDS.CLICK_CANCEL_FEE_EDIT;
ClickDeleteFee.data_event_id = EVENT_IDS.CLICK_DELETE_FEE;
ConfirmDeleteFee.data_event_id = EVENT_IDS.CONFIRM_DELETE_FEE;
ClickAddDisclaimer.data_event_id = EVENT_IDS.CLICK_ADD_DISCLAIMER;
ClickEditDisclaimer.data_event_id = EVENT_IDS.CLICK_EDIT_DISCLAIMER;
ClickSaveDisclaimer.data_event_id = EVENT_IDS.CLICK_SAVE_DISCLAIMER;
ClickViewFeeDetails.data_event_id = EVENT_IDS.CLICK_VIEW_FEE_DETAILS;

export default CostsAndFeesMFCSEvents;
